import React, { FC, useState, useCallback } from 'react';
import styled, { css } from 'styled-components';
import noop from 'lodash/noop';

import { ClickToEditField, EditableField } from 'src/presentation/components/agent_desktop_components';
import { LongTextWithTooltip } from 'src/presentation/components/pages/shared/LongTextWithTooltip';

type EditableIvrNameType = {
  dataAid: string;
  onSave: Function;
  value: string;
};

const IvrNameDisplayer = ({ dataAid, isDisabled, onClick, value }) => (
  <StyledIvrNameDisplayer data-aid={`${dataAid}-displayer`} isDisabled={isDisabled} onClick={onClick}>
    <LongTextWithTooltip value={value} />
  </StyledIvrNameDisplayer>
);

const IvrNameEditor = ({ dataAid, initialValue, onBlur, onCancel }) => (
  <StyledEditableField
    autoFocus
    dataAid={`${dataAid}-editor`}
    onBlur={onBlur}
    onCancel={onCancel}
    onSubmit={noop} // prevents double saving on 'enter' press 'cause it triggers two events: submit and blur
    placeholder="Type Name"
    value={initialValue}
  />
);

const ClickToEditIvrName = ClickToEditField(IvrNameDisplayer, IvrNameEditor);

const EditableIvrName: FC<EditableIvrNameType> = ({ dataAid, onSave, value }) => {
  const [isResponsePending, setIsResponsePending] = useState(false);

  const onSaveFn = useCallback(
    async value => {
      setIsResponsePending(true);
      await onSave(value);
      setIsResponsePending(false);
    },
    [onSave, setIsResponsePending]
  );

  return (
    <div data-aid={dataAid}>
      <ClickToEditIvrName dataAid={dataAid} isResponsePending={isResponsePending} onSave={onSaveFn} value={value} />
    </div>
  );
};

export default EditableIvrName;

const inputCss = css`
  position: absolute;
  left: 1px;
  top: 7px;
  background: none;
  border: 1px solid transparent;
  box-sizing: border-box;
  border-radius: 4px;
  padding: 10px;
  text-align: left;
  width: calc(100% - 2px);
`;

const StyledIvrNameDisplayer = styled.button`
  ${inputCss};
  &:hover {
    background: ${p => p.theme.colors.white};
    border: 1px solid ${p => p.theme.colors.gray600};
    cursor: text;
  }
`;

const StyledEditableField = styled(EditableField)`
  ${inputCss};
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray600};
`;
